import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { salvaBlob } from '../../utils/documenti';
import Icona from '../../components/cliente/Icone';
import '../../components/staff/numeri.css';

// /admin/numeri (solo admin): gli ultimi 12 mesi — clienti nuovi, visite fatte, proposte, rogiti; tempi medi; per
// persona dello staff; le agenzie con più visite e proposte; da dove arrivano i clienti; CSV. Niente dati sensibili.

const SERIE = [['clienti_nuovi', 'Clienti nuovi', 'oro'], ['visite', 'Visite fatte', 'blu'], ['proposte', 'Proposte', 'viola'], ['rogiti', 'Rogiti', 'verde']];
const FONTI = [['spontanea', 'Da soli'], ['codice_collaboratore', 'Con il codice di un collaboratore'], ['inseriti_dallo_staff', 'Inseriti dallo staff']];

const Barre = ({ mesi, chiave, nome, colore }) => {
  const massimo = Math.max(1, ...mesi.map((m) => m[chiave]));
  return (
    <section className="card r grafico" data-testid={`grafico-${chiave}`}>
      <div className="ttl"><h2>{nome} <small>{mesi.reduce((n, m) => n + m[chiave], 0)} in 12 mesi</small></h2></div>
      <div className="barre" role="img" aria-label={`${nome} per mese`}>
        {mesi.map((m) => (
          <div key={m.mese} className="colonna" title={`${m.etichetta}: ${m[chiave]}`}>
            <span className="valore">{m[chiave] || ''}</span>
            <i className={colore} style={{ height: `${(m[chiave] / massimo) * 100}%` }} />
            <small>{m.etichetta.split(' ')[0]}</small>
          </div>
        ))}
      </div>
    </section>
  );
};

const giorni = (n) => (n === null || n === undefined ? '—' : `${n} ${n === 1 ? 'giorno' : 'giorni'}`);

const Numeri = () => {
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(false);
  const [giro, setGiro] = useState(0);
  // i contatti non ancora clienti, per fonte: è la risposta a «i social servono?» (backend/contatti.py)
  const [contatti, setContatti] = useState(null);
  const [persona, setPersona] = useState('');
  const [persone, setPersone] = useState([]);
  // Se i numeri non arrivano, la pagina lo deve dire: prima restava su «Carico i numeri…» per sempre, con un
  // avviso che spariva da solo dopo qualche secondo (blocco 4, primo giro).
  useEffect(() => {
    let vivo = true;
    axios.get(`${API}/admin/numeri`, { params: persona ? { persona } : {} }).then((r) => {
      if (!vivo) return;
      setErrore(false);
      setDati(r.data);
      if (!persona) setPersone(r.data.per_persona);
    }).catch(() => { if (vivo) { setDati(null); setErrore(true); } });
    return () => { vivo = false; };
  }, [persona, giro]);
  useEffect(() => {
    axios.get(`${API}/contatti/numeri/per-fonte`)
      .then((r) => setContatti(r.data))
      .catch(() => setContatti(null));   // i contatti sono in più: se non arrivano, la pagina resta quella
  }, []);
  const csv = async () => {
    try {
      const r = await axios.get(`${API}/admin/numeri.csv`, { params: persona ? { persona } : {}, responseType: 'blob' });
      salvaBlob(r.data, 'numeri-mkclient.csv');
    } catch { toast.error('CSV non scaricato'); }
  };
  if (errore) {
    return (
      <main className="st wrap" data-testid="numeri-errore">
        <p className="vuoto" role="alert">Non riesco a caricare i numeri. Riprovate tra poco.</p>
        <p className="vuoto"><button type="button" className="btn sm" onClick={() => setGiro((g) => g + 1)}>Riprovate</button></p>
      </main>
    );
  }
  if (!dati) return <main className="st wrap"><p className="vuoto">Carico i numeri…</p></main>;
  const totFonti = FONTI.reduce((n, [k]) => n + dati.fonte[k], 0) || 1;
  return (
    <main className="st wrap pagina-numeri" data-testid="numeri">
      <div className="hero r">
        <div><span className="k">Ultimi 12 mesi</span><h1>I numeri</h1>
          <p>Solo conteggi e medie: niente nomi dei clienti né dati personali.</p></div>
        <button type="button" className="btn p" onClick={csv}><Icona nome="scarica" />CSV</button>
      </div>
      <div className="pills r" role="group" aria-label="Di chi">
        <button type="button" className={!persona ? 'on' : undefined} onClick={() => setPersona('')}>Tutti</button>
        {persone.map((p) => <button type="button" key={p.id} className={persona === p.id ? 'on' : undefined} onClick={() => setPersona(p.id)}>{p.nome}</button>)}
      </div>
      <div className="totali r">
        {SERIE.map(([k, n, c]) => <div key={k} className={c}><b>{dati.totali[k]}</b><span>{n.toLowerCase()}</span></div>)}
        <div><b>{giorni(dati.tempi.registrazione_proposta)}</b><span>dalla registrazione alla proposta{dati.tempi.casi_proposta ? ` (${dati.tempi.casi_proposta})` : ''}</span></div>
        <div><b>{giorni(dati.tempi.registrazione_rogito)}</b><span>dalla registrazione al rogito{dati.tempi.casi_rogito ? ` (${dati.tempi.casi_rogito})` : ''}</span></div>
      </div>
      <div className="griglia-grafici">
        {SERIE.map(([k, n, c]) => <Barre key={k} mesi={dati.mesi} chiave={k} nome={n} colore={c} />)}
      </div>
      <div className="g2">
        <section className="card r">
          <div className="ttl"><div className="i"><Icona nome="persone" /></div><h2>Per persona</h2></div>
          <div className="tabella" role="table">
            <div className="riga testa" role="row"><span>Chi</span><span>Clienti</span><span>Nuovi</span><span>Visite</span><span>Proposte</span><span>Rogiti</span></div>
            {dati.per_persona.map((p) => (
              <div className="riga" role="row" key={p.id}><span><i className={`pallino ${p.colore || ''}`} />{p.nome}</span><span>{p.clienti}</span>
                <span>{p.clienti_nuovi}</span><span>{p.visite}</span><span>{p.proposte}</span><span>{p.rogiti}</span></div>
            ))}
          </div>
        </section>
        <section className="card r">
          <div className="ttl"><div className="i b"><Icona nome="edificio" /></div><h2>Agenzie più produttive</h2></div>
          {!dati.agenzie.length && <p className="vuoto">Ancora nessuna visita o proposta.</p>}
          <div className="tabella" role="table">
            {dati.agenzie.length > 0 && <div className="riga testa quattro" role="row"><span>Agenzia</span><span>Comune</span><span>Visite</span><span>Proposte</span></div>}
            {dati.agenzie.map((a) => <div className="riga quattro" role="row" key={a.id}><span>{a.nome}</span><span>{a.comune || '—'}</span><span>{a.visite}</span><span>{a.proposte}</span></div>)}
          </div>
        </section>
      </div>
      <section className="card r" style={{ marginTop: '1rem' }}>
        <div className="ttl"><div className="i w"><Icona nome="freccia" /></div><h2>Da dove arrivano i clienti <small>registrati negli ultimi 12 mesi</small></h2></div>
        <div className="fonti">
          {FONTI.map(([k, n]) => (
            <div key={k}><span>{n}</span><div className="barra"><i style={{ width: `${(dati.fonte[k] / totFonti) * 100}%` }} /></div><b>{dati.fonte[k]}</b></div>
          ))}
        </div>
      </section>
      {contatti && contatti.totale > 0 && (
        <section className="card r" style={{ marginTop: '1rem' }} data-testid="numeri-contatti">
          <div className="ttl"><div className="i b"><Icona nome="persone" /></div>
            <h2>Contatti non ancora clienti <small>{contatti.totale} in tutto · {contatti.clienti} diventati clienti</small></h2></div>
          {/* la stessa tabella delle altre due della pagina (.tabella .riga): il .tbl dello staff sul telefono
              tiene solo la prima e l'ultima colonna, e qui sarebbe rimasto il tasso senza il nome della fonte */}
          <div className="tabella" role="table">
            <div className="riga testa" role="row"><span>Da dove</span><span>Contatti</span><span>Diventati</span><span>Persi</span><span>Aperti</span><span>Tasso</span></div>
            {contatti.per_fonte.map((f) => (
              <div className="riga" role="row" key={f.fonte} data-testid={`fonte-${f.fonte}`}>
                <span>{f.fonte_testo}</span>
                <span>{f.contatti}</span>
                <span>{f.clienti}</span>
                <span>{f.persi}</span>
                <span>{f.aperti}</span>
                {/* il tasso si calcola su quelli decisi: con nessuno deciso non si dice un numero a caso */}
                <span>{f.tasso === null ? '—' : `${f.tasso}%`}</span>
              </div>
            ))}
          </div>
          <p className="muto" style={{ marginTop: '.6rem' }}>
            Da contatto a cliente: {giorni(contatti.giorni_medi)} in media
            {contatti.giorni_contati ? ` (su ${contatti.giorni_contati})` : ''}.
          </p>
        </section>
      )}
    </main>
  );
};

export default Numeri;
